import React from "react";
import { Box, Link, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { VoicesPostState } from "../../../Enums/voices";
import { VoicesPost } from "../../../Models/voicesPosts";
import VoicesField from "../Common/VoicesField";
import { formatPostTime } from "./VoicesPostRows";

interface Props {
  post: VoicesPost;
}

const authorName = (post: VoicesPost) =>
  post.author ? `${post.author.name} ${post.author.surname}`.trim() : null;

const VoicesPostDetails: React.FC<Props> = ({ post }) => {
  const { t } = useTranslation("voicesPosts");
  const { history } = post;

  const rows: [string, React.ReactNode][] = [
    [
      t("details_created"),
      history.external
        ? t("details_imported")
        : `${authorName(post) ?? "–"} · ${history.created_at ? formatPostTime(history.created_at) : "–"}`,
    ],
    [
      t("details_written"),
      history.ai_drafted_at
        ? t("details_writtenAi", { time: formatPostTime(history.ai_drafted_at) })
        : t("details_writtenByHand"),
    ],
    [
      t("details_lastEdit"),
      history.edited_at
        ? `${history.edited_by ?? "–"} · ${formatPostTime(history.edited_at)}`
        : "–",
    ],
  ];

  if (post.campaign_name) rows.push([t("details_campaign"), post.campaign_name]);
  if (post.topic) rows.push([t("topic"), post.topic.title]);
  post.approvals.forEach((approval) =>
    rows.push([
      approval.approved
        ? t("details_approved")
        : t(approval.stopped ? "details_stopped" : "details_rejected"),
      `${approval.user}${approval.at ? ` · ${formatPostTime(approval.at)}` : ""}`,
    ]),
  );
  if (post.in_buffer || history.publish_attempts > 0) {
    rows.push([
      t("details_buffer"),
      post.in_buffer
        ? t("details_inBuffer", { count: history.publish_attempts })
        : t("details_attempts", { count: history.publish_attempts }),
    ]);
  }
  if (post.state === VoicesPostState.PUBLISHED) {
    rows.push([
      t("details_published"),
      post.linkedin_url ? (
        <Link href={post.linkedin_url} target="_blank" rel="noopener noreferrer" sx={{ color: color.orange600 }}>
          {post.scheduled_at ? formatPostTime(post.scheduled_at) : t("engage_open")}
        </Link>
      ) : (
        post.scheduled_at && formatPostTime(post.scheduled_at)
      ),
    ]);
  }

  return (
    <VoicesField label={t("details_title")}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          columnGap: 2,
          rowGap: 0.5,
          p: 1.5,
          borderRadius: 2,
          border: `1px solid ${color.gray200}`,
          backgroundColor: color.gray50,
        }}
      >
        {rows.map(([label, value], index) => (
          <React.Fragment key={`${label}-${index}`}>
            <Typography variant="caption" sx={{ color: color.gray600, whiteSpace: "nowrap" }}>
              {label}
            </Typography>
            <Typography variant="caption" sx={{ color: color.gray900, minWidth: 0, wordBreak: "break-word" }}>
              {value}
            </Typography>
          </React.Fragment>
        ))}
      </Box>
    </VoicesField>
  );
};

export default VoicesPostDetails;
